// Màn Dữ liệu (SAA-2026-008 UI2.1–UI2.3): bảng / form / chi tiết SINH TỪ MANIFEST, bộ lọc → SecretAAExpression, cursor,
// bộ chọn scope, bản ghi liên quan (relation ngược), « Đã xoá » + khôi phục. Không có chữ cứng: mọi nhãn qua i18n hoặc
// `label` của manifest.
import { RecordCommandRecovery } from "./record-command-recovery.tsx";
import { RecordCommandForm } from "./record-command-form.tsx";
import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, DataTable, Dialog, EmptyState, type FieldValue, FilterBuilder, type FilterCondition, RecordDetail, RecordForm, ScopePicker, separatorsOf, toExpression } from "@secretaa/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { api } from "../../app/api.ts";
import { useCurrentWorkspace } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { FormError, PageHeader } from "../../shared/form.tsx";
import { useBlueprint, useScopes } from "../project/project-hooks.ts";
import { ProjectQueryPanel } from "../project-query/project-query-panel.tsx";
import { RoleNeeded } from "../project/role-hint.tsx";
import { useRecordLabels } from "./data-hooks.ts";
import { activeFields, displayFieldOf, fieldSpec, formatValue, labelOf, type MCollection, type MField } from "./manifest-view.ts";

function useInputLabels() {
  const { t } = useTranslation();
  return { yes: t("common.yes"), no: t("common.no"), none: t("data.field.none"), invalidMoney: t("data.field.invalidMoney") };
}

/** Collection đích của các field relation ACTIVE (để dựng lựa chọn + nhãn). */
function relationTargets(collections: readonly MCollection[], c: MCollection) {
  const ids = new Set(activeFields(c).filter((f) => f.type === "relation").map((f) => (f.config as { targetCollectionId: string }).targetCollectionId));
  return collections.filter((x) => ids.has(x.id));
}

export function DataIndexPage() {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const { projectId = "" } = useParams();
  const bp = useBlueprint(projectId);
  if (bp.isPending) return <p>{t("app.loading")}</p>;
  if (bp.isError) return <FormError message={errorMessage(t, bp.error)} />;
  const first = bp.data.manifest?.collections.find((c) => c.status === "active");
  if (first) return <Navigate to={first.key} replace />;
  return (
    <EmptyState
      title={t("data.empty.noBlueprint")}
      description={t("data.empty.noBlueprintHint")}
      action={
        <div className="flex flex-col items-center gap-1">
          <Button asChild>
            <Link to={`/w/${cur.workspaceId}/projects/${projectId}/build`}>{t("build.start")}</Link>
          </Button>
          <RoleNeeded projectId={projectId} needs="manager" />
        </div>
      }
    />
  );
}

export function DataPage() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const qc = useQueryClient();
  const { projectId = "", collectionKey = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const bp = useBlueprint(projectId);
  const scopes = useScopes(projectId);
  const sep = separatorsOf(i18n.language);
  const inputLabels = useInputLabels();
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);
  const [sortOverride, setSortOverride] = useState<{ field: string; dir: "asc" | "desc" } | null>(null);
  const [creating, setCreating] = useState(false);
  const [createLocked, setCreateLocked] = useState(false);
  const [createRecovery, setCreateRecovery] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, FieldValue>>({});
  const manifest = bp.data?.manifest ?? null;
  const collections = manifest?.collections ?? [];
  const collection = collections.find((c) => c.key === collectionKey && c.status === "active");
  const targets = collection ? relationTargets(collections, collection) : [];
  const lookups = useRecordLabels(projectId, targets);
  const views = manifest?.views.filter((v) => v.collectionId === collection?.id) ?? [];
  const view = views.find((v) => v.key === params.get("view")) ?? views[0];
  const fields = collection ? activeFields(collection) : [];
  const viewFields = view ? view.fields.map((id) => fields.find((f) => f.id === id)).filter((f): f is MField => !!f) : fields;
  const columns = viewFields.length ? viewFields : fields;
  const userFilter = toExpression(conditions, fields.filter((f) => f.indexed).map((f) => fieldSpec(f, i18n.language)));
  const parts = [view?.filter ?? null, userFilter].filter((x) => x !== null && x !== undefined);
  const filter = parts.length === 0 ? undefined : parts.length === 1 ? parts[0] : { op: "and", args: parts };
  const sort = sortOverride ?? view?.sort ?? { field: "createdAt", dir: "desc" as const };
  const q = { filter, sort, scopeId: scopeId ?? undefined, deleted };
  const records = useInfiniteQuery({
    queryKey: qk.records(cur.tenant, projectId, collection?.id ?? "", q),
    queryFn: ({ pageParam }) => api.data.query(cur.workspaceId, projectId, collection!.id, { ...q, limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: "",
    getNextPageParam: (last) => last.meta.page.nextCursor ?? undefined,
    enabled: !!collection,
    staleTime: STALE_TIME.records,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.records(cur.tenant, projectId, collection?.id ?? "").slice(0, 5) });
  const restore = useMutation({
    mutationFn: (r: { id: string; version: number }) => api.data.restore(cur.workspaceId, projectId, collection!.id, r.id, r.version),
    onSuccess: invalidate,
  });
  if (bp.isPending) return <p>{t("app.loading")}</p>;
  if (bp.isError) return <FormError message={errorMessage(t, bp.error)} />;
  if (!collection) return <Navigate to=".." relative="path" replace />;
  const rows = records.data?.pages.flatMap((p) => p.data) ?? [];
  const show = (f: MField, v: FieldValue | undefined) => formatValue(f, v, i18n.language, sep, t, lookups.labelOf);
  const base = `/w/${cur.workspaceId}/projects/${projectId}/data/${collection.key}`;
  return (
    <>
      <PageHeader
        title={labelOf(collection.label, i18n.language)}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" aria-expanded={queryOpen} onClick={() => setQueryOpen(!queryOpen)}>{t("projectQuery.title")}</Button>
            {!queryOpen ? <><Button variant={deleted ? "primary" : "secondary"} aria-pressed={deleted} onClick={() => setDeleted(!deleted)}>
              {t("data.deleted.toggle")}
            </Button>
            {!deleted ? <Button disabled={createRecovery} onClick={() => setCreating(true)}>{t("data.record.new")}</Button> : null}
            <RoleNeeded projectId={projectId} needs="editor" /></> : null}
          </div>
        }
      />
      <RecordCommandRecovery workspaceId={cur.workspaceId} projectId={projectId} collectionId={collection.id} recordId={null} visible={!creating} onPending={setCreateRecovery} onRecovered={async()=>{await invalidate();setCreating(false);setCreateLocked(false);setDraft({});}}/>
      {queryOpen && manifest && bp.data.manifestHash ? <ProjectQueryPanel key={`${cur.workspaceId}:${cur.tenant.accessScopeVersion}:${projectId}:${bp.data.manifestHash}`} projectId={projectId} collections={collections} manifestHash={bp.data.manifestHash} initialCollection={collection} scopes={scopes.data ?? []} onRefresh={async () => { const result = await bp.refetch(); if (result.error) throw result.error; }} onClose={() => setQueryOpen(false)} /> : null}
      {!queryOpen ? <><nav aria-label={t("data.collections")} className="mb-3 flex flex-wrap gap-2">
        {collections
          .filter((c) => c.status === "active")
          .map((c) => (
            <Button key={c.id} asChild variant={c.id === collection.id ? "primary" : "ghost"}>
              <Link to={`../${c.key}`} relative="path" aria-current={c.id === collection.id ? "page" : undefined}>
                {labelOf(c.label, i18n.language)}
              </Link>
            </Button>
          ))}
      </nav>
      {views.length > 1 ? (
        <div role="tablist" aria-label={t("data.views")} className="mb-3 flex flex-wrap gap-2">
          {views.map((v) => (
            <Button key={v.id} role="tab" aria-selected={v.id === view?.id} variant={v.id === view?.id ? "secondary" : "ghost"} onClick={() => (setSortOverride(null), setParams({ view: v.key }))}>
              {labelOf(v.label, i18n.language)}
            </Button>
          ))}
        </div>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[14rem_1fr]">
        {scopes.data && scopes.data.length > 0 ? (
          <Card className="p-3">
            <ScopePicker label={t("scope.picker")} allLabel={t("scope.all")} scopes={scopes.data} selected={scopeId} onSelect={setScopeId} />
          </Card>
        ) : null}
        <div className="flex min-w-0 flex-col gap-3 lg:col-start-2">
          <Card className="p-4">
            <FilterBuilder
              fields={fields.filter((f) => f.indexed).map((f) => fieldSpec(f, i18n.language, lookups.byCollection.get((f.config as { targetCollectionId?: string }).targetCollectionId ?? "") ?? []))}
              conditions={conditions}
              onChange={setConditions}
              separators={sep}
              labels={{ ...inputLabels, title: t("data.filter.title"), field: t("data.filter.field"), operator: t("data.filter.operator"), add: t("data.filter.add"), remove: t("data.filter.remove"), op: (op) => t(`data.filter.ops.${op}`) }}
            />
          </Card>
          {records.isError ? <FormError message={errorMessage(t, records.error)} /> : null}
          {restore.isError ? <FormError message={errorMessage(t, restore.error)} /> : null}
          {records.isPending ? (
            <p>{t("app.loading")}</p>
          ) : rows.length === 0 ? (
            <EmptyState title={deleted ? t("data.deleted.empty") : t("data.empty.noRecords")} />
          ) : (
            <DataTable
              caption={labelOf(collection.label, i18n.language)}
              sortLabel={(h) => t("data.table.sortBy", { field: h })}
              onSort={(id) => {
                const f = columns.find((x) => x.id === id);
                if (!f?.indexed) return;
                setSortOverride({ field: id, dir: sort.field === id && sort.dir === "asc" ? "desc" : "asc" });
              }}
              columns={[
                ...columns.map((f) => ({ id: f.id, header: labelOf(f.label, i18n.language), sortable: f.indexed, sort: sort.field === f.id ? sort.dir : null })),
                ...(deleted ? [{ id: "_actions", header: t("data.table.actions") }] : []),
              ]}
              rows={rows.map((r) => ({
                id: r.id,
                cells: [
                  ...columns.map((f, i) =>
                    i === 0 ? (
                      <Link key={f.id} className="font-medium underline-offset-2 hover:underline" to={`${base}/${r.id}`}>
                        {show(f, r.values[f.id])}
                      </Link>
                    ) : (
                      show(f, r.values[f.id])
                    ),
                  ),
                  ...(deleted
                    ? [
                        <Button key="restore" variant="secondary" disabled={restore.isPending} onClick={() => restore.mutate({ id: r.id, version: r.version })}>
                          {t("data.deleted.restore")}
                        </Button>,
                      ]
                    : []),
                ],
              }))}
            />
          )}
          {records.hasNextPage ? (
            <Button variant="secondary" disabled={records.isFetchingNextPage} onClick={() => void records.fetchNextPage()}>
              {t("common.loadMore")}
            </Button>
          ) : null}
        </div>
      </div>
      </> : null}
      <Dialog open={creating} onOpenChange={(open) => { if (!createLocked) setCreating(open); }} title={t("data.record.new")} closeLabel={t("common.close")}>
        {creating && bp.data.manifestHash ? <RecordCommandForm key={`${cur.workspaceId}:${projectId}:${collection.id}`} workspaceId={cur.workspaceId} projectId={projectId} collectionId={collection.id} manifestHash={bp.data.manifestHash} operation={{kind:'CREATE'}} input={{scopeId,values:Object.fromEntries(Object.entries(draft).filter(([,v])=>v!==null))}} onLocked={setCreateLocked} onSuccess={async()=>{await invalidate();setCreating(false);setCreateLocked(false);setDraft({});}}>
          <RecordForm
            fields={fields.map((f) => fieldSpec(f, i18n.language, lookups.byCollection.get((f.config as { targetCollectionId?: string }).targetCollectionId ?? "") ?? []))}
            values={draft}
            onChange={(id, v) => setDraft((d) => ({ ...d, [id]: v }))}
            labels={inputLabels}
            separators={sep}
          />
          {scopes.data && scopes.data.length > 0 ? (
            <label className="flex flex-col gap-1.5 text-sm font-medium">
              {t("scope.picker")}
              <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={scopeId ?? ""} onChange={(e) => setScopeId(e.target.value || null)}>
                <option value="">{t("scope.none")}</option>
                {scopes.data.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </RecordCommandForm> : null}
      </Dialog>
    </>
  );
}

export function RecordPage() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const qc = useQueryClient();
  const { projectId = "", collectionKey = "", recordId = "" } = useParams();
  const bp = useBlueprint(projectId);
  const sep = separatorsOf(i18n.language);
  const inputLabels = useInputLabels();
  const [edit, setEdit] = useState<Record<string, FieldValue> | null>(null);
  const [saveLocked, setSaveLocked] = useState(false);
  const [saveRecovery, setSaveRecovery] = useState(false);
  const collections = bp.data?.manifest?.collections ?? [];
  const collection = collections.find((c) => c.key === collectionKey);
  const lookups = useRecordLabels(projectId, collection ? relationTargets(collections, collection) : []);
  const record = useQuery({
    queryKey: qk.record(cur.tenant, projectId, collection?.id ?? "", recordId),
    queryFn: () => api.data.get(cur.workspaceId, projectId, collection!.id, recordId),
    enabled: !!collection,
    staleTime: STALE_TIME.records,
  });
  // Bản ghi liên quan: mọi collection có field relation ACTIVE trỏ về collection này.
  const referrers = collections.flatMap((c) =>
    c.status === "active" ? activeFields(c).filter((f) => f.type === "relation" && (f.config as { targetCollectionId: string }).targetCollectionId === collection?.id).map((f) => ({ c, f })) : [],
  );
  const related = useQuery({
    queryKey: qk.records(cur.tenant, projectId, collection?.id ?? "", { relatedTo: recordId }),
    queryFn: async () =>
      Promise.all(
        referrers.map(async ({ c, f }) => ({ c, f, rows: (await api.data.query(cur.workspaceId, projectId, c.id, { filter: { op: "eq", left: { field: f.id }, right: { literal: recordId } }, limit: 50 })).data })),
      ),
    enabled: !!collection && referrers.length > 0,
    staleTime: STALE_TIME.records,
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: qk.record(cur.tenant, projectId, collection?.id ?? "", recordId) });
    await qc.invalidateQueries({ queryKey: qk.records(cur.tenant, projectId, collection?.id ?? "").slice(0, 5) });
  };
  const toggleDeleted = useMutation({
    mutationFn: () =>
      record.data!.deletedAt
        ? api.data.restore(cur.workspaceId, projectId, collection!.id, recordId, record.data!.version)
        : api.data.remove(cur.workspaceId, projectId, collection!.id, recordId, record.data!.version),
    onSuccess: refresh,
  });
  // A disabled record query remains pending when Blueprint cannot resolve the collection.
  if (bp.isPending) return <p>{t("app.loading")}</p>;
  if (bp.isError) return <FormError message={errorMessage(t, bp.error)} />;
  if (!collection) return <Navigate to="../.." relative="path" replace />;
  if (record.isPending) return <p>{t("app.loading")}</p>;
  if (record.isError) return <FormError message={errorMessage(t, record.error)} />;
  const r = record.data;
  const fields = activeFields(collection);
  const show = (f: MField, v: FieldValue | undefined) => formatValue(f, v, i18n.language, sep, t, lookups.labelOf);
  const display = displayFieldOf(collection);
  const title = display ? show(display, r.values[display.id]) : t("data.record.untitled");
  return (
    <>
      <PageHeader
        title={title}
        actions={
          <div className="flex gap-2">
            {!r.deletedAt && !edit ? (
              <Button variant="secondary" disabled={saveRecovery} onClick={() => setEdit({})}>
                {t("common.edit")}
              </Button>
            ) : null}
            <Button variant={r.deletedAt ? "primary" : "danger"} disabled={toggleDeleted.isPending || saveLocked || saveRecovery} onClick={() => toggleDeleted.mutate()}>
              {r.deletedAt ? t("data.deleted.restore") : t("data.record.delete")}
            </Button>
          </div>
        }
      />
      <RecordCommandRecovery workspaceId={cur.workspaceId} projectId={projectId} collectionId={collection.id} recordId={recordId} visible={!edit} onPending={setSaveRecovery} onRecovered={async()=>{await refresh();setEdit(null);setSaveLocked(false);}}/>
      <FormError message={toggleDeleted.isError ? errorMessage(t, toggleDeleted.error) : null} />
      {r.deletedAt ? <p className="mb-3 text-sm text-(--saa-danger)">{t("data.deleted.badge")}</p> : null}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          {edit && bp.data.manifestHash ? (
            <RecordCommandForm key={`${cur.workspaceId}:${projectId}:${collection.id}:${recordId}`} workspaceId={cur.workspaceId} projectId={projectId} collectionId={collection.id} manifestHash={bp.data.manifestHash} operation={{kind:'UPDATE',recordId,expectedVersion:r.version}} input={{values:edit}} onLocked={setSaveLocked} onCancel={()=>setEdit(null)} onSuccess={async()=>{await refresh();setEdit(null);setSaveLocked(false);}}>
              <RecordForm
                fields={fields.map((f) => fieldSpec(f, i18n.language, lookups.byCollection.get((f.config as { targetCollectionId?: string }).targetCollectionId ?? "") ?? []))}
                values={{ ...r.values, ...edit }}
                onChange={(id, v) => setEdit((d) => ({ ...(d ?? {}), [id]: v }))}
                labels={inputLabels}
                separators={sep}
              />
            </RecordCommandForm>
          ) : (
            <RecordDetail title={title} items={fields.map((f) => ({ id: f.id, label: labelOf(f.label, i18n.language), value: show(f, r.values[f.id]) }))} />
          )}
        </Card>
        {referrers.length ? (
          <section aria-label={t("data.related.title")} className="flex flex-col gap-4">
            <h2 className="text-base font-semibold">{t("data.related.title")}</h2>
            {(related.data ?? []).map(({ c, f, rows }) => {
              const d = displayFieldOf(c) ?? activeFields(c).find((x) => x.id !== f.id);
              return (
                <Card key={`${c.id}-${f.id}`} className="p-4">
                  <h3 className="mb-2 text-sm font-medium">
                    {labelOf(c.label, i18n.language)} · {t("data.related.count", { count: rows.length })}
                  </h3>
                  {rows.length === 0 ? (
                    <p className="text-sm text-(--saa-fg-muted)">{t("data.related.none")}</p>
                  ) : (
                    <ul className="flex flex-col gap-1 text-sm">
                      {rows.map((x) => (
                        <li key={x.id}>
                          <Link className="underline-offset-2 hover:underline" to={`/w/${cur.workspaceId}/projects/${projectId}/data/${c.key}/${x.id}`}>
                            {d ? formatValue(d, x.values[d.id], i18n.language, sep, t, lookups.labelOf) : x.id.slice(0, 8)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              );
            })}
          </section>
        ) : null}
      </div>
    </>
  );
}
